import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { user as userApi } from '../services/api';
import { useAuth } from './AuthContext';

type DebugContextValue = {
  isEnabled: boolean;
  isForced: boolean;
  isUpdating: boolean;
  canToggle: boolean;
  isAdmin: boolean;
  setEnabled: (enabled: boolean) => Promise<void>;
  toggle: () => Promise<void>;
};

const DebugContext = createContext<DebugContextValue | undefined>(undefined);

const DEBUG_STORAGE_KEY = 'awf:debug-mode';

type DebugProviderProps = React.PropsWithChildren<{
  initialEnabled?: boolean;
}>;

const readStoredOverride = (): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(DEBUG_STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  return stored === 'true';
};

const clearStoredOverride = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(DEBUG_STORAGE_KEY);
};

const persistStoredOverride = (enabled: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(DEBUG_STORAGE_KEY, String(enabled));
};

export const DebugProvider: React.FC<DebugProviderProps> = ({
  children,
  initialEnabled = false,
}) => {
  const { user, updateUser } = useAuth();
  const location = useLocation();

  const isAdmin = Boolean(user?.is_staff);
  const serverEnabled = Boolean(user?.debug_enabled && isAdmin);

  const [explicitEnabled, setExplicitEnabled] = useState<boolean>(() => Boolean(initialEnabled));
  const [localOverride, setLocalOverride] = useState<boolean | null>(null);
  const [isForced, setIsForced] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    if (!isAdmin) {
      setExplicitEnabled(false);
      return;
    }
    setExplicitEnabled(serverEnabled);
  }, [isAdmin, serverEnabled]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!isAdmin) {
      setLocalOverride(null);
      clearStoredOverride();
      return;
    }

    setLocalOverride(readStoredOverride());
  }, [user, isAdmin]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsForced(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const forced = params.get('debug') === '1' && isAdmin;
    setIsForced(forced);
  }, [location.search, isAdmin]);

  const isEnabled = useMemo(() => {
    if (!isAdmin) {
      return false;
    }

    if (isForced) {
      return true;
    }

    if (typeof localOverride === 'boolean') {
      return localOverride;
    }

    return explicitEnabled;
  }, [explicitEnabled, isAdmin, isForced, localOverride]);

  const setEnabled = useCallback(
    async (nextEnabled: boolean) => {
      if (!isAdmin) {
        return;
      }

      if (isForced) {
        return;
      }

      setExplicitEnabled(nextEnabled);
      setLocalOverride(null);
      clearStoredOverride();
      setIsUpdating(true);

      try {
        const response = await userApi.updateDebugMode(nextEnabled);
        const normalized = Boolean(response?.debug_enabled && isAdmin);
        setExplicitEnabled(normalized);
        updateUser({ debug_enabled: normalized });
      } catch (error) {
        setLocalOverride(nextEnabled);
        persistStoredOverride(nextEnabled);
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [isAdmin, isForced, updateUser],
  );

  const toggle = useCallback(() => setEnabled(!isEnabled), [isEnabled, setEnabled]);

  const contextValue = useMemo<DebugContextValue>(
    () => ({
      isEnabled,
      isForced,
      isUpdating,
      canToggle: isAdmin && !isForced,
      isAdmin,
      setEnabled,
      toggle,
    }),
    [isAdmin, isEnabled, isForced, isUpdating, setEnabled, toggle],
  );

  return <DebugContext.Provider value={contextValue}>{children}</DebugContext.Provider>;
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};
